import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const useNotifications = ({ user, elevatedChild, isKiosk }) => {
  const [notifications, setNotifications] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  const uid = elevatedChild?.id || user?.id;
  const isParent = user?.type === 'parent' && !isKiosk;

  // Fetch persistent notifications from DB on mount and periodically
  useEffect(() => {
    if (!uid) return;
    const fetchNotifications = async () => {
      try {
        const res = await api.get('/notifications');
        const dbNotifs = (res.data || []).map(n => ({
          id: n.id,
          title: n.title,
          body: n.body,
          notifType: n.type,
          timestamp: n.created_at,
          read: n.read,
          data: n.data ? JSON.parse(n.data) : null,
          persistent: true,
        }));
        setNotifications(dbNotifs);
      } catch {
        setNotifications([]);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [uid]);

  // Real-time: prepend WebSocket shop/approval events for instant feedback
  useEffect(() => {
    if (!uid) return;
    const handleShopItem = (event) => {
      const { item } = event.detail;
      if (!item) return;
      setNotifications(prev => {
        if (prev.some(n => n.notifType === 'shop_item_added' && n.data?.itemId === item.id)) return prev;
        return [{ id: `ws_${Date.now()}`, title: 'Uusi tuote kaupassa! 🛍️', body: `${item.name} (${item.price} kolikkoa)`, notifType: 'shop_item_added', timestamp: new Date().toISOString(), data: { itemId: item.id }, persistent: false }, ...prev];
      });
    };
    const handleApproval = (event) => {
      const { title, body, notifType, targetUserId } = event.detail;
      if (targetUserId && String(targetUserId) !== String(uid)) return;
      setNotifications(prev => [{ id: `ws_${Date.now()}`, title, body, notifType, timestamp: new Date().toISOString(), persistent: false }, ...prev]);
    };
    window.addEventListener('shopItemAdded', handleShopItem);
    window.addEventListener('approvalNotification', handleApproval);
    return () => {
      window.removeEventListener('shopItemAdded', handleShopItem);
      window.removeEventListener('approvalNotification', handleApproval);
    };
  }, [uid]);

  // Fetch pending approvals for parents
  useEffect(() => {
    if (!isParent) { setPendingApprovals([]); return; }
    const fetchApprovals = async () => {
      try {
        const [tasksRes, shopRes, goalsRes] = await Promise.all([
          api.get('/tasks/pending-approvals'),
          api.get('/shop/purchases'),
          api.get('/shared-goals'),
        ]);
        const tasks = (tasksRes.data.approvals || []).map(t => ({ ...t, type: 'task', title: t.task_name || t.task_title || t.title, child_name: t.child_name || t.name, completionId: t.id }));
        const purchases = (shopRes.data || []).filter(p => p.status === 'odottaa').map(p => ({ ...p, type: 'purchase', title: p.item_name || p.title, child_name: p.child_name, purchaseId: p.id }));
        const goals = (goalsRes.data.goals || [])
          .filter(g => g.current_points >= g.target_points && !g.rewarded_at)
          .map(g => ({ ...g, type: 'shared_goal', title: g.name, child_name: `${g.current_points}/${g.target_points}p`, goalId: g.id }));
        setPendingApprovals([...tasks, ...purchases, ...goals]);
      } catch { setPendingApprovals([]); }
    };
    fetchApprovals();
    const interval = setInterval(fetchApprovals, 30000);
    return () => clearInterval(interval);
  }, [isParent]);

  const dismissNotification = useCallback(async (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (typeof id === 'number') {
      try { await api.delete(`/notifications/${id}`); } catch {}
    }
  }, []);

  const clearAllNotifications = useCallback(async () => {
    setNotifications([]);
    try { await api.delete('/notifications'); } catch {}
  }, []);

  const handleApprove = useCallback(async (item) => {
    if (item.type === 'task') {
      await api.post(`/tasks/${item.completionId || item.id}/approve`);
    } else if (item.type === 'shared_goal') {
      await api.post(`/shared-goals/${item.goalId || item.id}/reward`);
    } else {
      await api.put(`/shop/purchases/${item.purchaseId || item.id}/approve`);
    }
    setPendingApprovals(prev => prev.filter(a => !(a.id === item.id && a.type === item.type)));
  }, []);

  const handleReject = useCallback(async (item) => {
    if (item.type === 'task') {
      await api.post(`/tasks/${item.completionId || item.id}/reject`);
    } else if (item.type === 'shared_goal') {
      // For shared goals, rejecting just dismisses from the list (goal stays active)
      setPendingApprovals(prev => prev.filter(a => !(a.id === item.id && a.type === item.type)));
      return;
    } else {
      await api.put(`/shop/purchases/${item.purchaseId || item.id}/reject`);
    }
    setPendingApprovals(prev => prev.filter(a => !(a.id === item.id && a.type === item.type)));
  }, []);

  return {
    notifications,
    pendingApprovals,
    dismissNotification,
    clearAllNotifications,
    handleApprove,
    handleReject,
    badgeCount: notifications.filter(n => !n.read).length + pendingApprovals.length,
  };
};

export default useNotifications;
